/**
 * Retry Mechanism Utility
 * 
 * This utility provides intelligent retry logic for failed API requests
 * with exponential backoff and configurable retry conditions.
 */

import { ERROR_CONFIG } from '@configs/api.config';

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_CONFIG = {
  retries: 3,
  retryDelay: 1000, // Base delay in milliseconds
  retryDelayMultiplier: 2, // Exponential backoff multiplier
  maxRetryDelay: 30000, // Maximum delay between retries
  retryCondition: (error) => {
    // Don't retry on client errors (4xx) except for specific cases
    if (error.response?.status >= 400 && error.response?.status < 500) {
      // Retry on rate limiting and request timeout
      return [408, 429].includes(error.response.status);
    }
    
    // Retry on server errors (5xx) and network errors
    return true;
  }
};

/**
 * Calculate retry delay with exponential backoff and jitter
 * @param {number} attempt - Current attempt number (0-based)
 * @param {number} baseDelay - Base delay in milliseconds
 * @param {number} multiplier - Exponential backoff multiplier
 * @param {number} maxDelay - Maximum delay
 * @returns {number} Delay in milliseconds
 */
const calculateRetryDelay = (attempt, baseDelay, multiplier, maxDelay) => {
  // Exponential backoff: baseDelay * (multiplier ^ attempt)
  const exponentialDelay = baseDelay * Math.pow(multiplier, attempt);
  
  // Add jitter to prevent thundering herd problem
  const jitter = Math.random() * 0.1 * exponentialDelay;
  
  // Cap at maximum delay
  return Math.min(exponentialDelay + jitter, maxDelay);
};

/**
 * Create a retry wrapper for async functions
 * @param {Function} fn - Async function to retry
 * @param {Object} config - Retry configuration
 * @returns {Function} Wrapped function with retry logic
 */
export const withRetry = (fn, config = {}) => {
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  
  return async (...args) => {
    let lastError;
    
    for (let attempt = 0; attempt <= retryConfig.retries; attempt++) {
      try {
        const result = await fn(...args);
        
        // Log successful retry
        if (attempt > 0) {
          console.log(`✅ Request succeeded after ${attempt} retries`);
        }
        
        return result;
      } catch (error) {
        lastError = error;
        
        // Check if we should retry
        if (attempt === retryConfig.retries || !retryConfig.retryCondition(error)) {
          break;
        }
        
        // Calculate delay for next attempt
        const delay = calculateRetryDelay(
          attempt,
          retryConfig.retryDelay,
          retryConfig.retryDelayMultiplier,
          retryConfig.maxRetryDelay
        );
        
        console.log(`🔄 Request failed (attempt ${attempt + 1}/${retryConfig.retries + 1}), retrying in ${delay}ms...`, error.message);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // All retries exhausted
    console.error(`❌ Request failed after ${retryConfig.retries + 1} attempts`, lastError);
    throw lastError;
  };
};

/**
 * Create axios interceptor with retry logic
 * @param {Object} axiosInstance - Axios instance
 * @param {Object} config - Retry configuration
 * @returns {Object} Axios instance with retry interceptor
 */
export const addRetryInterceptor = (axiosInstance, config = {}) => {
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // ✅ FIX: Check if originalRequest exists before accessing properties
      if (!originalRequest) {
        console.warn('⚠️ Retry interceptor: No config object in error, cannot retry');
        return Promise.reject(error);
      }

      // Avoid infinite retry loops
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      // Check if we should retry
      if (!retryConfig.retryCondition(error)) {
        return Promise.reject(error);
      }

      // Initialize retry count
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 0;
      }

      // Check if we've exceeded max retries
      if (originalRequest._retryCount >= retryConfig.retries) {
        return Promise.reject(error);
      }

      // Mark as retry and increment count
      originalRequest._retry = true;
      originalRequest._retryCount++;

      // Calculate delay
      const delay = calculateRetryDelay(
        originalRequest._retryCount - 1,
        retryConfig.retryDelay,
        retryConfig.retryDelayMultiplier,
        retryConfig.maxRetryDelay
      );

      console.log(`🔄 Retrying request (${originalRequest._retryCount}/${retryConfig.retries}) in ${delay}ms: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`);

      // Wait and retry
      await new Promise(resolve => setTimeout(resolve, delay));

      return axiosInstance(originalRequest);
    }
  );
  
  return axiosInstance;
};

/**
 * Predefined retry configurations for different scenarios
 */
export const RETRY_CONFIGS = {
  // Conservative retry for critical operations
  CRITICAL: {
    retries: 5,
    retryDelay: 2000,
    retryDelayMultiplier: 2,
    maxRetryDelay: 60000,
    retryCondition: (error) => {
      // Only retry on server errors and network issues
      return !error.response || error.response.status >= 500;
    }
  },
  
  // Standard retry for most operations
  STANDARD: {
    retries: 3,
    retryDelay: 1000,
    retryDelayMultiplier: 2,
    maxRetryDelay: 30000,
    retryCondition: (error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) {
        return [408, 429].includes(error.response.status);
      }
      return true;
    }
  },
  
  // Quick retry for real-time operations
  REALTIME: {
    retries: 2,
    retryDelay: 500,
    retryDelayMultiplier: 1.5,
    maxRetryDelay: 5000,
    retryCondition: (error) => {
      // Only retry on network errors and rate limiting
      return !error.response || [429, 502, 503, 504].includes(error.response.status);
    }
  },
  
  // No retry for mutations to prevent duplicates
  NO_RETRY: {
    retries: 0,
    retryCondition: () => false
  }
};

/**
 * Smart retry configuration based on HTTP method
 * @param {string} method - HTTP method
 * @returns {Object} Appropriate retry configuration
 */
export const getSmartRetryConfig = (method) => {
  const upperMethod = method.toUpperCase();
  
  switch (upperMethod) {
    case 'GET':
    case 'HEAD':
    case 'OPTIONS':
      return RETRY_CONFIGS.STANDARD;
    
    case 'POST':
    case 'PUT':
    case 'PATCH':
    case 'DELETE':
      // Don't retry mutations by default to prevent duplicates
      return RETRY_CONFIGS.NO_RETRY;
    
    default:
      return RETRY_CONFIGS.STANDARD;
  }
};

/**
 * Create a retry-enabled axios instance
 * @param {Object} axiosInstance - Base axios instance
 * @param {Object} config - Retry configuration
 * @returns {Object} Axios instance with retry capability
 */
export const createRetryAxios = (axiosInstance, config = {}) => {
  const retryInstance = axiosInstance.create ? axiosInstance.create() : axiosInstance;
  return addRetryInterceptor(retryInstance, config);
};

/**
 * Utility to check if an error is retryable
 * @param {Error} error - Error object
 * @returns {boolean} Whether the error is retryable
 */
export const isRetryableError = (error) => {
  return DEFAULT_RETRY_CONFIG.retryCondition(error);
};

export default {
  withRetry,
  addRetryInterceptor,
  createRetryAxios,
  getSmartRetryConfig,
  isRetryableError,
  RETRY_CONFIGS,
  DEFAULT_RETRY_CONFIG
};
